import { useState, useEffect, useRef, useContext } from 'react'
import axios from 'axios'
import { InputNesting, TextareaNesting } from './common/Inputs/Input'
import { projectContext, loadingContext, urlValue, unfolder, openAlert, useTranslate } from '../lib'
import '../styles/sideVoice.scss'
import { Tooltip } from 'react-tooltip'
import { Alerts } from './common/Alerts'
import FormData from 'form-data';
import * as lamejs from '@breezystack/lamejs';
import { MediaRecorder, register } from 'extendable-media-recorder';
import { connect } from 'extendable-media-recorder-wav-encoder';


export function NewVoice({ selectedVoice, refresh, setRefresh, update, sidePanel, allProjects }) {

  const { t } = useTranslate();
  const [isRecording, setIsRecording] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useContext(loadingContext)
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [alert, setAlert] = useState({ title: '', visible: false })
  const mediaRecorderRef = useRef(null);
  const [name, setName] = useState(t('voice.new'));
  const [editName, setEditName] = useState(false)
  const [blobTemp, setBlobTemp] = useState(null);
  const [consentPhrase, setConsentPhrase] = useState('');
  const [instruction1, setInstruction1] = useState('');
  const [instruction2, setInstruction2] = useState('');
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [language, setLanguage] = useState('EN');
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioBlobs, setAudioBlobs] = useState(null);
  const [capturedStream, setCapturedStream] = useState(null);
  const hasRegistered = useRef(false);
  const [projectsVisible, setProjectsVisible] = useState(true);
  const [projectSearch, setProjectSearch] = useState(''); // Nuevo estado para búsqueda de proyectos
  const previewText = useRef('Hello, this is a sample text to preview how this voice sounds.');
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(new Audio());
  const progressIntervalRef = useRef(null);
  const languageLocaleMap = {
    'EN': 'en-US',
    'ES': 'es-MX',
    'FR': 'fr-FR',
    'DE': 'de-DE',
    'PT': 'pt-BR'
  };
  
  useEffect(() => {
    const registerEncoder = async () => {
      if (!hasRegistered.current) {
        try {
          await register(await connect());
          hasRegistered.current = true;
        } catch (error) {
          console.error('Failed to register encoder:', error);
        }
      }
    };
    registerEncoder();
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (sidePanel === 'new') {
      setName(t('voice.new'));
      setSelectedProjects([]);
    } else {
      setName(selectedVoice.name);
      setSelectedProjects(selectedVoice.projects?.map(p => p.id) || []);
    }
  }, [selectedVoice, sidePanel]);

  useEffect(() => {
    if (sidePanel === 'new') {
      getPhrases(language.toLowerCase());
    }
  }, [language, sidePanel]);

  // Filtrar proyectos basado en la búsqueda
  const filteredProjects = allProjects.filter(project =>
    project.name.toLowerCase().includes(projectSearch.toLowerCase())
  );

  const handleProjectChange = (projectId) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      }
      return [...prev, projectId];
    });
  };

  const handleCancel = () => {
    cleanForm()
    update();
  }

  const cleanForm = () => {
    setName('Customizable Voice Name')
    setBlobTemp(null);
    setActive(0);
    setDuration(0);
    setCurrentTime(0);
    setProgress(0);
  }

  const convertToMp3 = async (wavBlob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = function () {
        const arrayBuffer = this.result;
        try {
          const wavDecoder = lamejs.WavHeader.readHeader(new DataView(arrayBuffer));
          if (!wavDecoder || !wavDecoder.dataOffset || !wavDecoder.dataLen) {
            throw new Error('Invalid WAV header');
          }

          const wavSamples = new Int16Array(arrayBuffer, wavDecoder.dataOffset, wavDecoder.dataLen / 2);

          const mp3Encoder = new lamejs.Mp3Encoder(wavDecoder.channels, wavDecoder.sampleRate, 128);

          const mp3Buffer = mp3Encoder.encodeBuffer(wavSamples);

          const mp3Data = mp3Encoder.flush();

          const mp3BufferWithHeader = new Uint8Array(mp3Buffer.length + mp3Data.length);
          mp3BufferWithHeader.set(mp3Buffer, 0);
          mp3BufferWithHeader.set(mp3Data, mp3Buffer.length);

          const mp3Blob = new Blob([mp3BufferWithHeader], { type: 'audio/mp3' });
          resolve(mp3Blob);

        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = function (error) {
        reject(error);
      };

      reader.readAsArrayBuffer(wavBlob);
    });
  }

  const handleVoiceSubmit = async (audioFile) => {

    setLoading(true);


    try {
      const locale = languageLocaleMap[language] || 'en-US';
      let id_user = unfolder(localStorage.getItem('hashInfo'), 'IANestingKey').id;
      let token = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').token;
      let csrfToken = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').csrfToken
      if (sidePanel === 'edit') {
        // Para edición, solo enviamos nombre y proyectos
        await axios({
          method: 'post',
          url: `${urlValue}/studio/voice/${selectedVoice.id}/update`,
          headers: {
            'name': name,
            'user': id_user,
            'access-token': token,
            'projects': JSON.stringify(selectedProjects),
            'X-CSRF-Token': csrfToken
          },
          withCredentials: true,
        });
      } else {
        // Para creación, enviamos el audio y demás datos
        const mp3Blob = await convertToMp3(audioFile);
        const formData = new FormData();
        formData.append('audio', mp3Blob, 'compliance_audio.mp3');

        await axios({
          method: 'post',
          url: `${urlValue}/studio/voice`,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
            'name': name,
            'user': id_user,
            'access-token': token,
            'locale': locale,
            'projects': JSON.stringify(selectedProjects),
            'X-CSRF-Token': csrfToken
          },
          withCredentials: true,
        });
      }

      setLoading(false);
      update();
      openAlert('#alert-ac',
        () => setAlert({
          title: `${sidePanel === 'edit' ? t('alerts.voiceSaved') : t('alerts.voiceUpdated')}`,
          visible: true
        }),
        4000,
        () => setAlert({ title: '', visible: false })
      );
    } catch (error) {
      setLoading(false);
      console.error('Error:', error);
      openAlert('#alert-ac',
        () => setAlert({
          title: `${sidePanel === 'edit' ? t('alerts.voiceNotSaved') : t('alerts.voiceNotUpdated')}`,
          visible: true
        }),
        4000,
        () => setAlert({ title: '', visible: false })
      );
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
        }
      });
      setCapturedStream(stream);

      const mediaRecorderInstance = new MediaRecorder(stream, { mimeType: 'audio/wav' });
      setMediaRecorder(mediaRecorderInstance);

      const blobs = [];
      mediaRecorderInstance.addEventListener('dataavailable', event => {
        blobs.push(event.data);
      });

      mediaRecorderInstance.start();
      setAudioBlobs(blobs);
      setIsRecording(true);

    } catch (error) {
      console.error('Error during recording:', error);
    }
  };

  const stopRecording = () => {
    return new Promise(resolve => {
      if (!mediaRecorder) {
        resolve(null);
        return;
      }

      mediaRecorder.addEventListener('stop', () => {
        const mimeType = mediaRecorder.mimeType;
        const audioBlob = new Blob(audioBlobs, { type: mimeType });
        const audioElement = document.createElement('audio');
        audioElement.src = URL.createObjectURL(audioBlob);
        audioElement.onloadedmetadata = () => {
          setDuration(audioElement.duration);
        };
        if (capturedStream) {
          capturedStream.getTracks().forEach(track => track.stop());
        }

        resolve(audioBlob);
      });

      mediaRecorder.stop();
      setIsRecording(false);
    });
  };

  const saveAudio = async () => {
    if (!name || name.trim() === '') {
      setAlert({ title: t('alerts.validName'), visible: true });
      setTimeout(() => setAlert({ title: '', visible: false }), 4000);
      return;
    }

    // Validar nombre sin caracteres especiales
    const nameRegex = /^[a-zA-Z0-9\s-_]+$/;
    if (!nameRegex.test(name)) {
      setAlert({
        title: t('alerts.validVoiceName'),
        visible: true
      });
      setTimeout(() => setAlert({ title: '', visible: false }), 4000);
      return;
    }

    // Validar que al menos un proyecto esté seleccionado
    if (selectedProjects.length === 0) {
      setAlert({
        title: t('alerts.selectOneProject'),
        visible: true
      });
      setTimeout(() => setAlert({ title: '', visible: false }), 4000);
      return;
    }
    if (sidePanel === 'edit') {
      // Para edición, llamamos directamente con null como audioFile
      await handleVoiceSubmit(null);
    } else {
      // Para creación, necesitamos el audio
      if (isRecording) {
        await stopRecording().then(blob => {
          setBlobTemp(blob);
          handleVoiceSubmit(blob);
        });
      } else if (!blobTemp) {
        setAlert({ title: t('alerts.noAudioRecorded'), visible: true });
        setTimeout(() => setAlert({ ...alert, visible: false }), 4000);
      } else {
        handleVoiceSubmit(blobTemp);
        setBlobTemp(null);
      }
    }
  };

  const playAudio = () => {
    if (blobTemp != null) {
      const audioUrl = URL.createObjectURL(blobTemp);
      const audio = new Audio(audioUrl);

      audio.play();
      const updateProgress = () => {
        setCurrentTime(audio.currentTime);
        setProgress((audio.currentTime / duration) * 100);
        if (audio.currentTime >= duration) {
          clearInterval(progressInterval);
        }
      };

      const progressInterval = setInterval(updateProgress, 100);
      audio.onended = () => {
        clearInterval(progressInterval);
        setCurrentTime(0);
        setProgress(0);
      };
    }
  };

  const playCustomVoice = async () => {
    if (!selectedVoice || !selectedVoice.speaker_profile_id) {
      setAlert({ title: t('alerts.voiceProfile'), visible: true });
      setTimeout(() => setAlert({ title: '', visible: false }), 4000);
      return;
    }

    setLoading(true);

    try {
      // Get Azure token
      let url = `${process.env.REACT_APP_AZURE_ENDPOINT}/sts/v1.0/issuetoken`;
      const resToken = await axios.post(url, {}, {
        headers: {
          'Ocp-Apim-Subscription-Key': process.env.REACT_APP_AZURE_SUBCRIPTION_KEY
        }
      });
      let token = resToken.data;

      // Default values for voice parameters
      const rate = "1%";
      const pitch = "1%";
      const style = "neutral";
      const volume = "+1.00%";

      // Create SSML for custom voice
      let dataPersonalVoice = `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis"
                              xmlns:mstts="https://www.w3.org/2001/mstts" xml:lang="en-US">
                              <voice name="DragonLatestNeural">
                                <mstts:ttsembedding speakerProfileId="${selectedVoice.speaker_profile_id}">
                                  <prosody rate="${rate}" pitch="${pitch}" volume="${volume}">
                                      <mstts:express-as style="${style}">
                                      ${previewText?.current.value || 'Test voice'}
                                      </mstts:express-as>
                                  </prosody>
                                </mstts:ttsembedding> 
                              </voice>
                            </speak>`;

      // Call Azure TTS API
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Ocp-Apim-Subscription-Key': process.env.REACT_APP_AZURE_SUBCRIPTION_KEY,
        'Content-Type': 'application/ssml+xml',
        'X-Microsoft-OutputFormat': 'audio-16khz-32kbitrate-mono-mp3'
      };

      const response = await axios.post(
        'https://eastus.tts.speech.microsoft.com/cognitiveservices/v1',
        dataPersonalVoice,
        {
          headers,
          responseType: 'arraybuffer'
        }
      );

      // Process the ArrayBuffer response
      const audioBlob = new Blob([response.data], { type: 'audio/mp3' });
      const audioUrl = URL.createObjectURL(audioBlob);

      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      }

      // Create new audio object
      audioRef.current = new Audio(audioUrl);

      // Set up duration and progress tracking
      audioRef.current.onloadedmetadata = () => {
        setDuration(audioRef.current.duration);
        audioRef.current.play();
        setIsPlaying(true);

        // Update progress bar
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }

        progressIntervalRef.current = setInterval(() => {
          setCurrentTime(audioRef.current.currentTime);
          setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
        }, 100);
      };

      // Handle audio end
      audioRef.current.onended = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        setProgress(0);
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      };

    } catch (error) {
      console.error('Error playing custom voice:', error);
      setAlert({ title: t('alerts.errorPlayingVoice'), visible: true });
      setTimeout(() => setAlert({ title: '', visible: false }), 4000);
    } finally {
      setLoading(false);
    }
  };

  // Function to pause custom voice playback
  const pauseCustomVoice = () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    }
  };

  const handleContenidoChange = (e) => {
    setName(e.target.value)
  }

  const handleEditName = () => {
    setEditName(true)
  }

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  useEffect(() => {
    getPhrases('en');
  }, []);

  const changeLanguage = async (e) => {
    const language = e.target.value;
    setLanguage(language)
    await getPhrases(language.toLowerCase());
  };

  const getPhrases = async (language) => {
    try {
      let token = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').token
      let csrfToken = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').csrfToken
      let id = unfolder(localStorage.getItem('hashInfo'), 'IANestingKey').id;
      const res = await axios.post(
        `${urlValue}/studio/project/phrasescustom`,
        { language: language },
        {
          headers: {
            'access-token': token,
            user: id,
            'X-CSRF-Token': csrfToken
          },
          withCredentials: true,
        }
      );

      if (res.status === 200) {
        setConsentPhrase(res.data?.data);
        setInstruction1(t('menu.newVoiceInstruction'));
        setInstruction2(t('menu.newVoiceInstruction2'));
      } else {
        console.error('Error fetching phrases:', res.data.message);
      }
    } catch (error) {
      console.error('Network error:', error);
    }
  };

  return (
    <div id="voices">
      <div className="Voice">
        <div className="scroll-internal-intents">
          <div className="IntTitle">
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              style={{ width: "95%" }}
            />
            <button onClick={() => setEditName(true)}>
              <img src="/icons/edit-icon.svg" alt={t('menu.editName')} />
            </button>
          </div>

          {sidePanel === 'new' && (
            <>
              <div className="select" style={{ maxWidth: 183, marginTop: 5 }}>
                <select
                  id="language"
                  className="w-100 select-nesting"
                  style={{ backgroundPositionX: "98%" }}
                  onChange={changeLanguage}
                  value={language}
                >
                  <option value="EN">{t('common.english')}</option>
                  <option value="ES">{t('common.spanish')}</option>
                  <option value="FR">{t('common.french')}</option>
                  <option value="DE">{t('common.german')}</option>
                  <option value="PT">{t('common.portuguese')}</option>
                </select>
              </div>

              <div className="Labels">
                <label>{instruction1}</label>
              </div>
              <div className='Phrases'>
                <div>
                  <p>{consentPhrase}</p>
                </div>
                <div>
                  <button className='buttonRecording' onClick={() => isRecording ? stopRecording().then(blob => setBlobTemp(blob)) : startRecording()}>
                    {isRecording ?
                      <img src="/icons/record-btn-hover.png" alt="Start Recording" />
                      :
                      <img src="/icons/record-btn.png" alt="Stop Recording" />
                    }
                  </button>
                </div>
                <div className="Labels">
                  <label>{instruction2}</label>
                </div>
                <div className='reproduceAudio' style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px', // Más espacio entre elementos
                  padding: '10px 0'
                }}>
                  <button onClick={playAudio} style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px', // Más padding para botones más grandes
                    borderRadius: '50%',
                    transition: 'background-color 0.2s'
                  }}>
                    <img 
                      src="/icons/play-icon.svg" 
                      alt="Play" 
                      style={{ width: '20px', height: '20px' }} // Botones más grandes
                    />
                  </button>
                  <button style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px', // Más padding para botones más grandes
                    borderRadius: '50%',
                    transition: 'background-color 0.2s'
                  }}>
                    <img 
                      src="/icons/pause-icon.svg" 
                      alt="Pause" 
                      style={{ width: '20px', height: '20px' }} // Botones más grandes
                    />
                  </button>
                  <div className="timer" style={{ 
                    minWidth: '45px',
                    fontSize: '20px',
                    fontWeight: '500'
                  }}>
                    {formatTime(currentTime)}
                  </div>
                  <progress 
                    className="custom-progress" 
                    max={duration} 
                    value={currentTime}
                    style={{
                      flex: 1,
                      height: '6px',
                      borderRadius: '3px'
                    }}
                  />
                  <div className="timer" style={{ 
                    minWidth: '45px',
                    fontSize: '20px',
                    fontWeight: '500'
                  }}>
                    {formatTime(duration)}
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="projects-section" style={{ marginBottom: '20px', marginTop: sidePanel === 'new' ? '30px' : '20px' }}>
            <h3
              onClick={() => setProjectsVisible(!projectsVisible)}
              style={{ cursor: 'pointer' }}
            >
              <span style={{
                transform: projectsVisible ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
                display: 'inline-block',
                margin: '5px'
              }}>▶</span>
              {t('menu.assignToProjects')}
            </h3>

            {projectsVisible && (
              <div style={{
                border: '1px solid #eee',
                borderRadius: '8px',
                backgroundColor: '#fafafa'
              }}>
                {/* Buscador de proyectos */}
                <div style={{ 
                  padding: '10px', 
                  borderBottom: '1px solid #eee' 
                }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder={t('common.search')}
                      value={projectSearch}
                      onChange={(e) => setProjectSearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '20px',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                    <img 
                      src="/icons/search.svg" 
                      alt="Search"
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '16px',
                        height: '16px',
                        opacity: 0.5
                      }}
                    />
                  </div>
                </div>
                
                {/* Lista de proyectos filtrados */}
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  padding: '10px'
                }}>
                  {filteredProjects.length > 0 ? (
                    filteredProjects.map(project => (
                      <div key={project.id} className="project-item" style={{
                        padding: '8px 0',
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedProjects.includes(project.id)}
                          onChange={() => handleProjectChange(project.id)}
                          id={`project-${project.id}`}
                          style={{ marginRight: '10px' }}
                        />
                        <label htmlFor={`project-${project.id}`} style={{
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}>
                          {project.name}
                        </label>
                      </div>
                    ))
                  ) : (
                    <div style={{ 
                      padding: '20px', 
                      textAlign: 'center', 
                      color: '#666',
                      fontSize: '14px'
                    }}>
                      {t('voice.noProjectsFound') || 'No se encontraron proyectos'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {sidePanel === 'edit' && selectedVoice && selectedVoice.speaker_profile_id && (
            <div className="voice-preview-section" style={{ marginTop: '20px' }}>
              <h3>{t('menu.voicePreview')}</h3>
              <div style={{ marginBottom: '20px' }}>
                <TextareaNesting
                  ref={previewText} id="preview-text"
                  row={3}
                  containerClass="mt-2"
                  placeholder={t('menu.voicePreviewTooltip')}
                />
              </div>
              <div className="audio-player" style={{
                padding: '15px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={isPlaying ? pauseCustomVoice : playCustomVoice}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px'
                  }}
                >
                  <img
                    src={isPlaying ? "/icons/pause-icon.svg" : "/icons/play-icon.svg"}
                    alt={isPlaying ? "Pause" : "Play"}
                    style={{ width: '24px', height: '24px' }}
                  />
                </button>

                <div className="timer" style={{ minWidth: '40px', color: '#0078d7' }}>
                  {formatTime(currentTime)}
                </div>

                <div style={{ flex: 1, position: 'relative' }}>
                  <progress
                    className="custom-progress"
                    max={duration}
                    value={currentTime}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      appearance: 'none'
                    }}
                  ></progress>
                </div>

                <div className="timer" style={{ minWidth: '40px', color: '#0078d7' }}>
                  {formatTime(duration)}
                </div>
              </div>
            </div>
          )}

          <div className="buttons">
            <button id='cancel-tp' className="create btn-bottom btn-bottom-cancel" onClick={(handleCancel)} style={{ minWidth: "auto" }}>{t('common.cancel')}</button>
            <Tooltip anchorSelect="#cancel-tp" content={t('intents.cancelCreation')} place={"top"} />

            <button
              id='save-tp'
              className="create btn-bottom btn-bottom-create"
              onClick={saveAudio}
              style={{ marginLeft: 10, minWidth: "auto" }}
            >
              {sidePanel === 'edit' ? t('common.update') : t('common.create')}
            </button>
            <Tooltip anchorSelect="#save-tp" content={t('intents.saveChangesTooltip')} place={"top"} />
          </div>
        </div>

      </div>
      {alert.visible && <Alerts id="alert-ac" title={alert.title} />}
    </div>
  );
}